import firebaseLib from "firebase/app";
import "firebase/database";
import { all } from "underscore";

const db = () => {
  try {
    return firebaseLib.database();
  } catch (e) {
    console.warn("Firebase not ready yet:", e.message);
    return null;
  }
};

export async function createNotification(targetUserId, payload) {
  if (!targetUserId || !payload) return;

  const notifRef = db().ref(`notifications/${targetUserId}`).push();
  await notifRef.set({
    ...payload,
    id: notifRef.key,
    isRead: false,
    createdAt: Date.now(),
    navigateTo: "/leave",
  });
}

export async function markAsRead(userId, notifId) {
  if (!userId || !notifId) return;
  await db().ref(`notifications/${userId}/${notifId}`).update({ isRead: true });
}

export async function markAllAsRead(userId) {
  if (!userId) return;
  const snapshot = await db().ref(`notifications/${userId}`).once("value");
  if (!snapshot.exists()) return;

  const updates = {};
  snapshot.forEach((child) => {
    if (!child.val().isRead) {
      updates[`${child.key}/isRead`] = true;
    }
  });
  if (Object.keys(updates).length) {
    await db().ref(`notifications/${userId}`).update(updates);
  }
}

/**
 * Real-time listener for a user's notifications (most recent 50).
 * Returns an unsubscribe function — call it in beforeDestroy().
 *
 * @param {string}   userId
 * @param {function} callback  receives array of notifications, newest first
 * @returns {function} unsubscribe
 */
export function listenToNotifications(userId, callback) {
  if (!userId) return function() {};

  var ref = db()
    .ref("notifications/" + userId)
    .orderByChild("createdAt")
    .limitToLast(50);

  var handler = function(snapshot) {
    var items = [];
    snapshot.forEach(function(child) {
      items.push(child.val());
    });
    callback(items.reverse());
  };

  ref.on("value", handler);

  return function() { ref.off("value", handler); };
}

/**
 * Parse a leave activity object and fan-out the correct notification(s).
 *
 * @param {object} activity        The activity you already saved to Firebase
 * @param {object} allUsers        Map of uid => user object (from your users node)
 * @param {string} currentUserId   UID of the person performing the action
 */
export async function handleLeaveActivityNotification(
  activity,
  allUsers,
  currentUserId
) {
  if (!activity || activity.type !== "leaves") return;

  var activityOnUser      = activity.activityOnUser;
  var activityOnUserName  = activity.activityOnUserName;
  var activityByUserName  = activity.activityByUserName;
  var activityDescription = activity.activityDescription;

  if (!activityDescription) return;

  // ── Detect action type from description string ──────────────────────────
  var isApplied  = activityDescription.indexOf("applied for") !== -1;
  var isApproved = activityDescription.indexOf("approved") !== -1;
  var isRejected = activityDescription.indexOf("rejected") !== -1;

  if (!isApplied && !isApproved && !isRejected) return;

  // ── Extract leave details from description ──────────────────────────────
  var leaveTypeMatch = activityDescription.match(
    /(Casual|Sick|Emergency|Annual|Maternity|Paternity|Unpaid)\s+Leave/
  );
  var dateRangeMatch = activityDescription.match(
    /from\s+(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})/
  );
  var daysMatch = activityDescription.match(/\(([^)]+)\)/);

  var leaveType = leaveTypeMatch ? leaveTypeMatch[0] : "Leave";
  var leaveFrom = dateRangeMatch ? dateRangeMatch[1] : "";
  var leaveTo   = dateRangeMatch ? dateRangeMatch[2] : "";
  var leaveDays = daysMatch ? daysMatch[1] : "";

  // ── Find all admin UIDs to notify when employee applies ─────────────────
  var adminIds = Object.entries(allUsers || {})
    .filter(function(entry) {
      var u = entry[1];
      return u.role === "admin" || u.isSuperAdmin === true;
    })
    .map(function(entry) { return entry[0]; });

  if (isApplied) {
    var appliedPayload = {
      type:                "leave_applied",
      title:               "New Leave Request",
      message:             activity.activityName,
      activityType:        "leaves",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserId:   activityOnUser,
      performedByUserName: activityByUserName,
      leaveType:           leaveType,
      leaveFrom:           leaveFrom,
      leaveTo:             leaveTo,
      leaveDays:           leaveDays,
    };
    await notifyAdmins(allUsers, {
      ...appliedPayload,
      navigateTo:          "/leave",
    }, currentUserId);

  } else if (isApproved) {
    var approvedPayload = {
      type:                "leave_approved",
      title:               "Leave Approved",
      message:             activity.activityName,
      activityType:        "leaves",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserId:   currentUserId,
      performedByUserName: activityByUserName,
      leaveType:           leaveType,
      leaveFrom:           leaveFrom,
      leaveTo:             leaveTo,
      leaveDays:           leaveDays,
    };
    await createNotification(activityOnUser, approvedPayload);

  } else if (isRejected) {
    var rejectedPayload = {
      type:                "leave_rejected",
      title:               "Leave Rejected",
      message:             activity.activityName,
      activityType:        "leaves",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserId:   currentUserId,
      performedByUserName: activityByUserName,
      leaveType:           leaveType,
      leaveFrom:           leaveFrom,
      leaveTo:             leaveTo,
      leaveDays:           leaveDays,
    };
    await createNotification(activityOnUser, rejectedPayload);
  }
}

export async function requestNotificationPermission() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  var result = await Notification.requestPermission();
  return result;
}

/**
 * Show a native Chrome / browser notification.
 *
 * @param {string}   title
 * @param {object}   options   { body, icon, tag }
 * @param {function} onClick   called when user clicks the notification
 */
export function showChromeNotification(title, options, onClick) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;

  var notification = new Notification(title, {
    body:               (options && options.body) || "",
    icon:               (options && options.icon) || "/static/img/pertime-log-.png",
    tag:                (options && options.tag)  || "pertime-notif",
    requireInteraction: false,
    silent:             false,
  });

  if (typeof onClick === "function") {
    notification.onclick = function() {
      window.focus();
      onClick();
      notification.close();
    };
  }
}

function getAdminIds(allUsers) {
  return Object.entries(allUsers || {})
    .filter(function(entry) {
      var u = entry[1];
      return u.role === "admin" || u.isSuperAdmin === true;
    })
    .map(function(entry) { return entry[0]; });
}

async function createNotif(targetUserId, payload) {
  if (!targetUserId || !payload) return;
  var notifRef = db().ref("notifications/" + targetUserId).push();
  await notifRef.set({
    ...payload,
    id:        notifRef.key,
    isRead:    false,
    createdAt: Date.now(),
  });
}

async function notifyAdmins(allUsers, payload, excludeUserId) {
  var adminIds = getAdminIds(allUsers);
  await Promise.all(
    adminIds
      .filter(function(id) { return id !== excludeUserId; })  // ← add this line
      .map(function(id) { return createNotif(id, payload); })
  );
}

async function notifyAllUsers(allUsers, payload, excludeUserId) {
  const userIds = getAllUserIds(allUsers);

  await Promise.all(
    userIds
      .filter(function(id) { return id !== excludeUserId; })
      .map(function(id) { return createNotif(id, payload); })
  );
}

function getAllUserIds(allUsers) {
  const users = Object.values(allUsers || {});
  const ids = [];

  users.forEach(function(user) {
    if (user && user.uid) ids.push(user.uid);
  });

  return ids;
}

export async function handlePerformanceEditNotification(activity, allUsers, currentUserId) {
  if (!activity || activity.type !== "performance_edit") return;

  var activityOnUser     = activity.activityOnUser;
  var activityOnUserName = activity.activityOnUserName;
  var activityByUserName = activity.activityByUserName;
  var action             = (activity.action.trim() || "").toLowerCase();
  var cardName           = activity.cardName || "a performance item";
  var fieldLabel          = (activity.fieldLabel || "").toLowerCase();
  
  if (!activityOnUser) return;

  // 3 done (when admin update score itself)
  if (fieldLabel === "score" && action === "updated") {
    await createNotif(activityOnUser, {
      type:                "performance_score_updated",
      title:               "Performance Score Updated",
      message:             activity.activityName,
      activityType:        "performance_edit",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      cardName:            cardName,
      navigateTo:          "/performance",
    });
    return;
  }

  // 4 done (when admin approve employee's request to update reopen count)
  if (fieldLabel === "reopened count" && action === "approved") {
    await createNotif(activityOnUser, {
      type:                "performance_reopen_edit_approved",
      title:               "Performance Reopen Edit Approved",
      message:             activity.activityName,
      activityType:        "performance_edit",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      cardName:            cardName,
      navigateTo:          "/performance",
    });
    return;
  }

  // 3 done (when admin update reopen count itself)
  if (fieldLabel === "reopened count" && action === "updated") {
    await createNotif(activityOnUser, {
      type:                "performance_reopen_updated",
      title:               "Reopen Count Updated",
      message:             activity.activityName,
      activityType:        "performance_edit",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      cardName:            cardName,
      navigateTo:          "/performance",
    });
    return;
  }

  // 3 done (when employee request reopen count to admin)
  if (fieldLabel === "reopened count" && action === "requested") {

    await notifyAdmins(allUsers, {
      type:                "performance_reopen_requested",
      title:               "Reopen Count Edit Requested",
      message:             activity.activityName,
      activityType:        "performance_edit",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/performance",
    }, currentUserId);
    return;
  }

  // 3 done (when employee request to add/update item)
  if (action === "requested" || action === "updated request") {

    await notifyAdmins(allUsers, {
      type:                "performance_item_added",
      title:               "Performance Item Added",
      message:             activity.activityName,
      activityType:        "performance_edit",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/performance",
    }, currentUserId);
    return;
  }

  // 2 done
  if (action === "approved") {
    await createNotif(activityOnUser, {
      type:                "approved",
      title:               "Performance Item Approved",
      message:             activity.activityName || "Your performance item \"" + cardName + "\" was approved",
      activityType:        "performance_edit",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      cardName:            cardName,
      navigateTo:          "/performance",
    });
    return;
  }

  if (action === "rejected") {
    await createNotif(activityOnUser, {
      type:                "rejected",
      title:               "Performance Item Rejected",
      message:             activity.activityName || "Your performance item \"" + cardName + "\" was rejected",
      activityType:        "performance_edit",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      cardName:            cardName,
      navigateTo:          "/performance",
    });
    return;
  }

  if (action === "deleted") {
    if (activity.isAdmin) {
      await createNotif(activityOnUser, {
        type: "performance_item_deleted",
        title: "Performance Item Deleted",
        message: activity.activityName,
        activityType: "performance_edit",
        relatedUserId: activityOnUser,
        relatedUserName: activityOnUserName,
        performedByUserName: activityByUserName,
        cardName: cardName,
        navigateTo: "/performance",
      });
      return;
    } else {
      await notifyAdmins(allUsers, {
        type: "performance_item_deleted",
        title: "Performance Item Deleted",
        message: activity.activityName,
        activityType: "performance_edit",
        relatedUserId: activityOnUser,
        relatedUserName: activityOnUserName,
        performedByUserName: activityByUserName,
        navigateTo: "/performance",
      }, currentUserId);
      return;
    }
  }

  // 3 done (when admin add new item)
  if (action === "added") {
    await createNotif(activityOnUser, {
      type:                "performance_item_added",
      title:               "Performance Item Added",
      message:             activity.activityName,
      activityType:        "performance_edit",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      cardName:            cardName,
      navigateTo:          "/performance",
    });
    return;
  }

  // 2 done (when admin update item)
  if (action === "updated") {
    await createNotif(activityOnUser, {
      type:                "performance_item_updated",
      title:               "Performance Item Updated",
      message:             activity.activityName,
      activityType:        "performance_edit",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      cardName:            cardName,
      navigateTo:          "/performance",
    });
  }
}

export async function handlePerformanceLockUnlockNotification(activity, allUsers, currentUserId) {
  if (!activity || activity.type !== "performance_lock_unlock") return;

  var activityByUserName = activity.activityByUserName;
  var activityName       = activity.activityName || "";
  var desc               = activity.activityDescription || "";
  var action            = (activity.action || "").toLowerCase();

  // Notify all users except the one who performed the action
  await notifyAllUsers(allUsers, {
    type:                "performance_lock_unlock",
    title:               `Performance ${action === "locked" ? "Locked" : action === "unlocked" ? "Unlocked" : "Lock Scheduled"  }`,
    message:             activityName || desc,
    activityType:        "performance_lock_unlock",
    performedByUserName: activityByUserName,
    navigateTo:          "/performance",
  }, currentUserId);
}

export async function handlePerformanceLockNotification(activity) {
  if (!activity || activity.type !== "performance_lock") return;

  var activityOnUser     = activity.activityOnUser;
  var activityOnUserName = activity.activityOnUserName;
  var activityByUserName = activity.activityByUserName;
  var action             = activity.action || "";
  var month              = activity.month  || "";
  var year               = activity.year   || "";
  var period             = (month && year) ? month + " " + year : "";

  if (!activityOnUser) return;

  var typeMap = {
    "Locked Performance":         { type: "performance_locked",         title: "Performance Locked"          },
    "Unlocked Performance":       { type: "performance_unlocked",       title: "Performance Unlocked"        },
    "Scheduled Lock Performance": { type: "performance_scheduled_lock", title: "Performance Lock Scheduled"  },
  };

  var meta = typeMap[action];
  if (!meta) return;

  var periodText = period ? " for " + period : "";
  var messageMap = {
    "Locked Performance":         "Your performance" + periodText + " has been locked by " + activityByUserName,
    "Unlocked Performance":       "Your performance" + periodText + " has been unlocked by " + activityByUserName,
    "Scheduled Lock Performance": "Your performance" + periodText + " has a scheduled lock set by " + activityByUserName,
  };

  await createNotif(activityOnUser, {
    type:                meta.type,
    title:               meta.title,
    message:             messageMap[action],
    activityType:        "performance_lock",
    relatedUserId:       activityOnUser,
    relatedUserName:     activityOnUserName,
    performedByUserName: activityByUserName,
    month:               month,
    year:                year,
    navigateTo:          "/performance",
  });
}

export async function handleSettingsNotification(activity, allUsers, currentUserId) {
  if (!activity || activity.type !== "settings") return;

  var activityByUserName = activity.activityByUserName;
  var activityOnUser     = activity.activityOnUser || null; // null means general settings, userId means specific user settings
  var activityOnUserName = activity.activityOnUserName || null;
  var desc               = activity.activityDescription || "";
  var isSpecific         = desc.indexOf("specific settings") !== -1;

  if (isSpecific && activityOnUser) {
    // Specific user settings → notify that employee
    await createNotif(activityOnUser, {
      type:                "settings_updated",
      title:               "Your Settings Updated",
      message:             activityByUserName + " updated your account settings",
      activityType:        "settings",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/settings",
    });
  } else {
    // General settings → notify all users
    await notifyAllUsers(allUsers, {
      type:                "general_settings_updated",
      title:               "General Settings Updated",
      message:             activityByUserName + " updated the general system settings",
      activityType:        "settings",
      performedByUserName: activityByUserName,
      navigateTo:          "/settings",
    }, currentUserId);
  }
}

export async function handlePerformanceCriteriaNotification(activity, allUsers, currentUserId) {
  if (!activity || activity.type !== "performance_criteria") return;

  var activityByUserName = activity.activityByUserName;
  var activityOnUser     = activity.activityOnUser || null;
  var activityOnUserName = activity.activityOnUserName || null;
  var activityName        = activity.activityName || "";

  await notifyAllUsers(allUsers, {
    type: "performance_criteria_updated",
    title: `Performance Criteria ${activity.action}`,
    message: activityName,
    activityType: "performance_criteria",
    performedByUserName: activityByUserName,
    navigateTo: "/settings",
  }, currentUserId);
}

export async function handleManualHoursNotification(activity, allUsers, currentUserId) {
  if (!activity || activity.type !== "manual_hours") return;
 
  var activityOnUser     = activity.activityOnUser;
  var activityOnUserName = activity.activityOnUserName;
  var activityByUserName = activity.activityByUserName;
  var action             = (activity.action || "").toLowerCase();
  var desc               = activity.activityDescription || "";
 
  if (!activityOnUser) return;
 
  // Extract date  e.g. "for February 5, 2026"
  var dateMatch = desc.match(/for\s+([A-Za-z]+\s+\d+,\s+\d+)/);
  var dateText  = dateMatch ? dateMatch[1] : "";
 
  // Extract time range  e.g. "Time In: 09:00 AM and Time Out: 05:00 PM"
  var timeInMatch  = desc.match(/Time In:\s*([\d:]+\s*[AP]M)/i);
  var timeOutMatch = desc.match(/Time Out:\s*([\d:]+\s*[AP]M)/i);
  var timeRange    = (timeInMatch && timeOutMatch)
    ? " (" + timeInMatch[1] + " \u2192 " + timeOutMatch[1] + ")"
    : "";
 
  var datePart = (dateText ? " for " + dateText : "") + timeRange;
 
  // \u2500\u2500 action: added \u2014 admin manually added hours \u2192 notify employee \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  if (action === "added") {
    await createNotif(activityOnUser, {
      type:                "hours_added",
      title:               "Working Hours Added \u23f1\ufe0f",
      message:             activity.activityName,
      activityType:        "manual_hours",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/reports",
    });
    return;
  }
 
  // \u2500\u2500 action: updated \u2014 admin updated hours \u2192 notify employee \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  if (action === "updated") {
    await createNotif(activityOnUser, {
      type:                "hours_updated",
      title:               "Working Hours Updated \u23f1\ufe0f",
      message:             activity.activityName,
      activityType:        "manual_hours",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/reports",
    });
    return;
  }
 
  // \u2500\u2500 action: request \u2014 employee requested hours edit \u2192 notify all admins \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  if (action === "request") {
    await notifyAdmins(allUsers, {
      type:                "hours_edit_requested",
      title:               "Hours Edit Requested \u23f1\ufe0f",
      message:             activity.activityName,
      activityType:        "manual_hours",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/reports",
    }, currentUserId);
    return;
  }
 
  // \u2500\u2500 action: approve \u2014 admin approved request \u2192 notify employee \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  if (action === "approve") {
    await createNotif(activityOnUser, {
      type:                "hours_edit_approved",
      title:               "Hours Edit Approved \u2705",
      message:             activity.activityName,
      activityType:        "manual_hours",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/reports",
    });
    return;
  }
 
  // \u2500\u2500 action: reject \u2014 admin rejected request \u2192 notify employee \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  if (action === "reject") {
    await createNotif(activityOnUser, {
      type:                "hours_edit_rejected",
      title:               "Hours Edit Rejected \u274c",
      message:             activity.activityName,
      activityType:        "manual_hours",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/reports",
    });
  }
}

export async function handleMeetingNotification(activity, allUsers) {
  if (!activity) return;
  if (activity.type !== "quarterly" && activity.type !== "increment") return;

  var activityOnUser     = activity.activityOnUser;
  var activityOnUserName = activity.activityOnUserName;
  var activityByUserName = activity.activityByUserName;
  var desc               = activity.activityDescription || "";
  var meetingLabel       = activity.type === "quarterly" ? "Quarterly" : "Increment";

  var attendees = activity.attendees || [];
  var emailToUid = buildEmailToUidMap(allUsers || {});

  if (!activityOnUser) return;

  var isScheduled = desc.indexOf("scheduled") !== -1;
  var isDeleted   = desc.indexOf("deleted")   !== -1;

  if (!isScheduled && !isDeleted) return;

  // Extract meeting datetime  e.g. "on March 10, 2026 at 2:28 PM"
  var meetingDateMatch = desc.match(/on\s+([A-Za-z]+\s+\d+,\s+\d+\s+at\s+[\d:]+\s*[AP]M)/i);
  var meetingDate      = meetingDateMatch ? meetingDateMatch[1] : "";

  if (isScheduled) {
    await createNotif(activityOnUser, {
      type:                meetingLabel.toLowerCase() + "_meeting_scheduled",
      title:               meetingLabel + " Meeting Scheduled",
      message: activityByUserName +
        " scheduled a " +
        meetingLabel +
        " meeting for you on " +
        meetingDate,
      activityType:        activity.type,
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/usermanagement",
    });
  } else if (isDeleted) {
    await createNotif(activityOnUser, {
      type:                meetingLabel.toLowerCase() + "_meeting_deleted",
      title:               meetingLabel + " Meeting Cancelled",
      message: (activityByUserName + " cancelled a " + meetingLabel
        + " meeting for you on " + meetingDate),
      activityType:        activity.type,
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/usermanagement",
    });
  }

  var attendeeUids = (attendees || [])
    .map(function(email) {
      return emailToUid[email];
    })
    .filter(Boolean);
  
  var uniqueAttendees = [...new Set(attendeeUids)]
    .filter(function (uid) {
      return uid && uid !== activityOnUser;
    });
  console.log("Unique attendees to notify:", uniqueAttendees);

  if (uniqueAttendees.length) {
    await Promise.all(
      uniqueAttendees.map(function (uid) {
        if (!uid || uid === activityOnUser) return;

        return createNotif(uid, {
          type: isScheduled
            ? meetingLabel.toLowerCase() + "_meeting_invite"
            : meetingLabel.toLowerCase() + "_meeting_cancelled",

          title: isScheduled
            ? meetingLabel + " Meeting Invitation"
            : meetingLabel + " Meeting Cancelled",

          message: isScheduled
            ? (activityByUserName +
              " scheduled a " +
              meetingLabel +
              " meeting for " + activityOnUserName +
              " on " + meetingDate)
            : (activityByUserName + " cancelled a " + meetingLabel
              + " meeting for " + activityOnUserName
              + " on " + meetingDate),

          activityType: activity.type,
          relatedUserId: activityOnUser,
          relatedUserName: activityOnUserName,
          performedByUserName: activityByUserName,
          navigateTo: "/usermanagement",
        });
      })
    );
  }
}

function buildEmailToUidMap(allUsers) {
  return Object.values(allUsers || {}).reduce(function(map, user) {
    if (user && user.email && user.uid) {
      map[user.email] = user.uid;
    }
    return map;
  }, {});
}

export async function handleCreateUserNotification(activity, allUsers, currentUserId) {
  if (!activity || activity.type !== "create_user") return;

  var activityOnUser     = activity.activityOnUser;
  var activityOnUserName    = activity.activityOnUserName;
  var activityByUserName = activity.activityByUserName;
  var newUserName        = activity.activityOnUserName || "a new user";
  var desc               = activity.activityDescription || "";
  var activityName       = activity.activityName || "";

  // Extract email  e.g. "with email naeem.test3@expertizo.pk"
  var emailMatch = desc.match(/email\s+([\w.+-]+@[\w.+-]+)/);
  var email      = emailMatch ? emailMatch[1] : "";

  await notifyAdmins(allUsers, {
    type:                "user_created",
    title:               activity.role === "admin" ? "User Access Updated" : "New User Created 👤",
    message:             activityName,
    activityType:        "create_user",
    performedByUserName: activityByUserName,
    navigateTo:          "/usermanagement",
  }, currentUserId);

  await createNotif(activityOnUser, {
    type: "onboarded",
    title: `Welcome to the Team, ${newUserName}!`,
    message: `Hi ${newUserName}, your ${activity.role === "admin" ? "admin" : "user"} account has been created. We're excited to have you on board!`,
    activityType: "create_user",
    relatedUserId: activityOnUser,
    relatedUserName: activityOnUserName,
    performedByUserName: activityByUserName,
    navigateTo: "/user",
  });
}

export async function handleUpdateUserNotification(activity, allUsers, currentUserId) {
  if (!activity || activity.type !== "update_user") return;

  var activityOnUser     = activity.activityOnUser;
  var activityOnUserName    = activity.activityOnUserName;
  var activityByUserName = activity.activityByUserName;
  var desc               = activity.activityDescription || "";
  var activityName       = activity.activityName || "";

  // Extract email (optional)
  var emailMatch = desc.match(/email\s+([\w.+-]+@[\w.+-]+)/);
  var email      = emailMatch ? emailMatch[1] : "";

  // Normalize description for checks
  var lowerDesc = desc.toLowerCase();

  const isStatusChange =
    lowerDesc.includes("activated") || lowerDesc.includes("deactivated");

  const isRoleChange =
    lowerDesc.includes("admin") || lowerDesc.includes("role") || lowerDesc.includes("changed");

  if (isRoleChange) {
    await createNotif(activityOnUser, {
      type: "user_updated",
      title: `User Access Updated`,
      message:
        activityByUserName +
        `${lowerDesc.includes("admin") ? " granted you admin access" : " removed your admin access"}`,
      activityType: "update_user",
      relatedUserId: activityOnUser,
      relatedUserName: activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo: "/user",
    });
  }

  // ============================
  // 1. Admin-level changes
  // ============================
  if (isStatusChange || isRoleChange) {
    await notifyAdmins(allUsers, {
      type: "user_updated",
      title: "User Access Updated",
      message: activityName,
      activityType: "update_user",
      performedByUserName: activityByUserName,
      navigateTo: "/usermanagement",
    }, currentUserId); // already excludes current user

    return;
  }

  // ============================
  // 2. Other profile updates
  // ============================
  if (activity.isAdmin) {
  await createNotif(activityOnUser, {
    type: "profile_updated",
    title: "Your Profile Updated",
    message:
      activityByUserName +
      " updated your profile details",
    activityType: "update_user",
    relatedUserId: activityOnUser,
    relatedUserName: activityOnUserName,
    performedByUserName: activityByUserName,
    navigateTo: "/user",
  });
  } else {
    await notifyAdmins(allUsers, {
      type: "profile_updated",
      title: "User Profile Updated",
      message: activityName,
      activityType: "update_user",
      performedByUserName: activityByUserName,
      navigateTo: "/usermanagement",
    }, currentUserId);
  }
}

export async function handleDeleteUserNotification(activity, allUsers, currentUserId) {
  if (!activity || activity.type !== "delete_user") return;

  var activityByUserName = activity.activityByUserName;
  var deletedUserName    = activity.activityOnUserName || "a user";
  var desc               = activity.activityDescription || "";

  // Extract email  e.g. "with email urgent.sole.rzdk@hidingmail.com"
  var emailMatch = desc.match(/email\s+([\w.+-]+@[\w.+-]+)/);
  var email      = emailMatch ? emailMatch[1] : "";

  await notifyAdmins(allUsers, {
    type:                "user_deleted",
    title:               "User Account Deleted",
    message:             activityByUserName + " deleted the account of "
                         + deletedUserName + (email ? " (" + email + ")" : ""),
    activityType:        "delete_user",
    performedByUserName: activityByUserName,
    navigateTo:          "/usermanagement",
  }, currentUserId);
}

export async function handlePayrollNotification(activity) {
  if (!activity || activity.type !== "payroll") return;

  var activityOnUser     = activity.activityOnUser;
  var activityOnUserName = activity.activityOnUserName;
  var activityByUserName = activity.activityByUserName;
  var desc               = activity.activityDescription || "";

  if (!activityOnUser) return;

  var isProcessed = desc.indexOf("processed payroll")         !== -1;
  var isDraft     = desc.indexOf("saved the payroll draft")   !== -1
                 || desc.indexOf("updated the payroll draft") !== -1;
  var isDraftUpdate = desc.indexOf("updated the payroll draft") !== -1;

  if (!isProcessed && !isDraft) return;

  // Extract month and year  e.g. "for Adnan Rafiq for March 2026"
  var monthMatch = desc.match(/(January|February|March|April|May|June|July|August|September|October|November|December)/);
  var yearMatch  = desc.match(/(\d{4})/);
  var period     = (monthMatch ? monthMatch[1] + " " : "") + (yearMatch ? yearMatch[1] : "");

  if (isProcessed) {
    await createNotif(activityOnUser, {
      type:                "payroll_processed",
      title:               "Payroll Processed",
      message:             "Your payroll" + (period ? " for " + period : "")
                           + " has been processed by " + activityByUserName,
      activityType:        "payroll",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/payroll",
    });
  } else if (isDraft) {
    await createNotif(activityOnUser, {
      type:                "payroll_draft",
      title:               "Payroll Draft Saved",
      message:             "Your payroll draft" + (period ? " for " + period : "")
                           + " has been " + (isDraftUpdate ? "updated" : "saved") + " by " + activityByUserName,
      activityType:        "payroll",
      relatedUserId:       activityOnUser,
      relatedUserName:     activityOnUserName,
      performedByUserName: activityByUserName,
      navigateTo:          "/payroll",
    });
  }
}

// Employee added/updated/deleted one of their own expense adjustments → tell the admins
export async function handleExpenseNotification(activity, allUsers, currentUserId) {
  var action = activity.action || "added";
  var amount = Number(activity.amount) || 0;
  var period = [activity.month, activity.year].filter(Boolean).join(" ");
  var titles = { added: "New Expense Added", updated: "Expense Updated", deleted: "Expense Deleted" };

  var message = activity.activityByUserName + " " + action + " an expense of Rs. "
    + amount.toLocaleString("en-US") + (period ? " for " + period : "")
    + (activity.description ? ": " + activity.description : "");

  await notifyAdmins(allUsers, {
    type:                "expense_" + action,
    title:               titles[action] || "Expense",
    message:             message,
    activityType:        "expense",
    relatedUserId:       activity.activityOnUser,
    relatedUserName:     activity.activityOnUserName,
    performedByUserId:   currentUserId,
    performedByUserName: activity.activityByUserName,
    navigateTo:          "/payroll?tab=adjustments",
  }, currentUserId);
}

export async function handleActivityNotification(activity, authData, currentUserId) {
  if (!activity) return;
  const { company: { users: companyUsers }, allUsers } = authData || {};

  const companyUids = new Set(
    (companyUsers || [])
      .filter(u => u && u.uid)
      .map(u => u.uid)
  );

  const allUsersMap = (allUsers || []).reduce((acc, user) => {
    if (user && user.uid && companyUids.has(user.uid)) {
      acc[user.uid] = user;
    }
    return acc;
  }, {});

  switch (activity.type) {
    case "leaves":
      return handleLeaveActivityNotification(activity, allUsersMap, currentUserId);
    case "performance_edit":
      return handlePerformanceEditNotification(activity, allUsersMap, currentUserId);
    case "performance_lock_unlock":
      return handlePerformanceLockUnlockNotification(activity, allUsersMap, currentUserId);
    case "settings":
      return handleSettingsNotification(activity, allUsersMap, currentUserId);
    case "performance_criteria":
      return handlePerformanceCriteriaNotification(activity, allUsersMap, currentUserId);
    case "manual_hours":
      return handleManualHoursNotification(activity, allUsersMap, currentUserId);
    case "quarterly":
      return handleMeetingNotification(activity, allUsersMap);
    case "increment":
      return handleMeetingNotification(activity, allUsersMap);
    case "create_user":
      return handleCreateUserNotification(activity, allUsersMap, currentUserId);
    case "update_user":
      return handleUpdateUserNotification(activity, allUsersMap, currentUserId);
    case "delete_user":
      return handleDeleteUserNotification(activity, allUsersMap, currentUserId);
    case "payroll":
      return handlePayrollNotification(activity);
    case "expense":
      return handleExpenseNotification(activity, allUsersMap, currentUserId);
    default:
      return;
  }
}


// WEBPACK FOOTER //
// ./src/services/notificationService.js